import { relations } from 'drizzle-orm';
import { boolean, decimal, integer, index, pgEnum, pgTable, text, uuid } from 'drizzle-orm/pg-core';
import { Status } from '../../util/schemas.js';
import { user } from './auth.js';
import { drugCatalogItems, medicationProfiles } from './drug-catalog.js';
import { durationUnitEnum, expiryRuleTypeEnum, microbiologicalLimitationEnum, stabilityRuleSourceEnum, stabilityStageEnum, } from './enums.js';
import { timestamps } from './utils/index.js';
import { userActions } from './utils/user-actions.js';
import { uuidv7 } from './utils/uuidv7.js';
import { protocols } from './protocols.js';
export const basicDataStatus = pgEnum('basic_data_status', [Status.enum.active, Status.enum.inactive]);
export const units = pgTable('units', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    displayName: text('display_name').notNull(),
    ucumCode: text('ucum_code').notNull(),
    ucumNumericCode: text('ucum_numeric_code'),
    description: text('description'),
    notes: text('notes'),
    ...userActions(),
    ...timestamps(),
});
// `code` is nullable across basic-data tables: the CMS auto-generates it from
// the name, so users may leave it blank
export const diagnoses = pgTable('diagnoses', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    code: text('code').unique(),
    name: text('name').notNull(),
    description: text('description'),
    status: basicDataStatus('status').default(Status.enum.active),
    ...timestamps(),
    ...userActions(),
});
export const diagnosesRelations = relations(diagnoses, ({ one, many }) => ({
    protocols: many(protocols),
    created: one(user, {
        fields: [diagnoses.createdBy],
        references: [user.id],
    }),
}));
export const cancerTypes = pgTable('cancer_types', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    code: text('code').unique(),
    name: text('name').notNull(),
    description: text('description'),
    status: basicDataStatus('status').default(Status.enum.active),
    ...timestamps(),
    ...userActions(),
});
export const cancerTypesRelations = relations(cancerTypes, ({ one, many }) => ({
    protocols: many(protocols),
    created: one(user, {
        fields: [cancerTypes.createdBy],
        references: [user.id],
    }),
}));
export const adminRoutes = pgTable('administration_routes', {
    id: uuidv7('id').primaryKey(),
    code: text('code').unique(),
    name: text('name').notNull(),
    description: text('description'),
    status: basicDataStatus('status').default(Status.enum.active),
    ...timestamps(),
    ...userActions(),
});
export const adminRoutesRelations = relations(adminRoutes, ({ one }) => ({
    created: one(user, {
        fields: [adminRoutes.createdBy],
        references: [user.id],
    }),
}));
export const generalPhysicians = pgTable('general_physicians', {
    id: uuidv7('id').primaryKey(),
    code: text('code').unique(),
    name: text('name').notNull(),
    specialty: text('specialty'),
    phone: text('phone'),
    email: text('email'),
    description: text('description'),
    status: basicDataStatus('status').default(Status.enum.active),
    ...timestamps(),
    ...userActions(),
});
export const generalPhysiciansRelations = relations(generalPhysicians, ({ one }) => ({
    created: one(user, {
        fields: [generalPhysicians.createdBy],
        references: [user.id],
    }),
}));
export const applicationForms = pgTable('application_forms', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    code: text('code').unique(),
    name: text('name').notNull(),
    description: text('description'),
    preparationRequired: boolean('preparation_required'),
    carrierRequired: boolean('carrier_required'),
    status: basicDataStatus('status').default(Status.enum.active),
    ...timestamps(),
    ...userActions(),
});
export const applicationFormsRelations = relations(applicationForms, ({ one }) => ({
    created: one(user, {
        fields: [applicationForms.createdBy],
        references: [user.id],
    }),
}));
export const containerTypes = pgTable('container_types', {
    id: uuidv7('id').primaryKey(),
    code: text('code').unique(),
    name: text('name').notNull(),
    description: text('description'),
    status: basicDataStatus('status').default(Status.enum.active),
    ...timestamps(),
    ...userActions(),
});
export const containerTypesRelations = relations(containerTypes, ({ one }) => ({
    created: one(user, {
        fields: [containerTypes.createdBy],
        references: [user.id],
    }),
}));
export const containerMaterials = pgTable('container_materials', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    code: text('code').unique(),
    name: text('name').notNull(),
    description: text('description'),
    status: basicDataStatus('status').default(Status.enum.active),
    ...timestamps(),
    ...userActions(),
});
export const containerMaterialsRelations = relations(containerMaterials, ({ one }) => ({
    created: one(user, {
        fields: [containerMaterials.createdBy],
        references: [user.id],
    }),
}));
export const hazardClasses = pgTable('hazard_classes', {
    id: uuidv7('id').primaryKey(),
    code: text('code').unique(),
    name: text('name').notNull(),
    description: text('description'),
    status: basicDataStatus('status').default(Status.enum.active),
    ...timestamps(),
    ...userActions(),
});
export const hazardClassesRelations = relations(hazardClasses, ({ one }) => ({
    created: one(user, {
        fields: [hazardClasses.createdBy],
        references: [user.id],
    }),
}));
export const solutionTypes = pgTable('solution_types', {
    id: uuidv7('id').primaryKey(),
    code: text('code').unique(),
    name: text('name').notNull(),
    description: text('description'),
    status: basicDataStatus('status').default(Status.enum.active),
    ...timestamps(),
    ...userActions(),
});
export const solutionTypesRelations = relations(solutionTypes, ({ one }) => ({
    created: one(user, {
        fields: [solutionTypes.createdBy],
        references: [user.id],
    }),
}));
export const stockLocations = pgTable('stock_locations', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    code: text('code').unique(),
    name: text('name').notNull(),
    description: text('description'),
    status: basicDataStatus('status').default(Status.enum.active),
    ...timestamps(),
    ...userActions(),
});
export const stockLocationsRelations = relations(stockLocations, ({ one }) => ({
    created: one(user, {
        fields: [stockLocations.createdBy],
        references: [user.id],
    }),
}));
export const companies = pgTable('companies', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    code: text('code').unique(),
    name: text('name').notNull(),
    description: text('description'),
    status: basicDataStatus('status').default(Status.enum.active),
    ...timestamps(),
    ...userActions(),
});
export const companiesRelations = relations(companies, ({ one }) => ({
    created: one(user, {
        fields: [companies.createdBy],
        references: [user.id],
    }),
}));
export const insuranceCompanies = pgTable('insurance_companies', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    code: text('code').unique(),
    name: text('name').notNull(),
    description: text('description'),
    nationalId: text('national_id'),
    status: basicDataStatus('status').default(Status.enum.active),
    ...timestamps(),
    ...userActions(),
});
export const insuranceCompaniesRelations = relations(insuranceCompanies, ({ one }) => ({
    created: one(user, {
        fields: [insuranceCompanies.createdBy],
        references: [user.id],
    }),
}));
export const treatmentChairs = pgTable('treatment_chairs', {
    id: uuidv7('id').primaryKey(),
    code: text('code').unique(),
    name: text('name').notNull(),
    description: text('description'),
    status: basicDataStatus('status').default(Status.enum.active),
    ...timestamps(),
    ...userActions(),
});
export const treatmentChairsRelations = relations(treatmentChairs, ({ one }) => ({
    created: one(user, {
        fields: [treatmentChairs.createdBy],
        references: [user.id],
    }),
}));
// #region Medicine forms
export const medicineForms = pgTable('medicine_forms', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    code: text('code').unique(),
    name: text('name').notNull(),
    description: text('description'),
    reconstitutionRequired: boolean('reconstitution_required'),
    isDrySubstance: boolean('is_dry_substance'),
    status: basicDataStatus('status').default(Status.enum.active),
    ...timestamps(),
    ...userActions(),
});
export const medicineFormsRelations = relations(medicineForms, ({ one, many }) => ({
    medicationProfiles: many(medicationProfiles),
    created: one(user, {
        fields: [medicineForms.createdBy],
        references: [user.id],
    }),
}));
// #endregion
// #region Drugs categories
export const drugsCategories = pgTable('drugs_categories', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    name: text('name').notNull(),
    description: text('description'),
    status: basicDataStatus('status').default(Status.enum.active),
    parentId: uuid('parent_id').references(() => drugsCategories.id, { onDelete: 'set null' }), // subcategories have parentId populated
    ...timestamps(),
    ...userActions(),
});
export const drugCatalogItemsCategories = pgTable('drug_catalog_items_categories', {
    id: uuidv7('id').primaryKey(),
    drugCatalogItemId: uuid('drug_catalog_item_id')
        .notNull()
        .references(() => drugCatalogItems.id, { onDelete: 'cascade' }),
    drugCategoryId: uuid('drug_category_id')
        .notNull()
        .references(() => drugsCategories.id, { onDelete: 'cascade' }),
    ...timestamps(),
    ...userActions(),
}, t => [index('idx_drug_catalog_items_categories_item_category').on(t.drugCatalogItemId, t.drugCategoryId)]);
export const drugsCategoriesRelations = relations(drugsCategories, ({ one, many }) => ({
    parent: one(drugsCategories, {
        fields: [drugsCategories.parentId],
        references: [drugsCategories.id],
    }),
    children: many(drugsCategories),
    drugCatalogItemsCategories: many(drugCatalogItemsCategories),
    created: one(user, {
        fields: [drugsCategories.createdBy],
        references: [user.id],
    }),
}));
export const drugCatalogItemsCategoriesRelations = relations(drugCatalogItemsCategories, ({ one }) => ({
    drugCategory: one(drugsCategories, {
        fields: [drugCatalogItemsCategories.drugCategoryId],
        references: [drugsCategories.id],
    }),
    drugCatalogItem: one(drugCatalogItems, {
        fields: [drugCatalogItemsCategories.drugCatalogItemId],
        references: [drugCatalogItems.id],
    }),
}));
// #endregion
// #region Stability rules
export const storageLabels = pgTable('storage_labels', {
    id: uuidv7('id').primaryKey(),
    code: text('code').unique(),
    name: text('name').notNull(),
    description: text('description'),
    status: basicDataStatus('status').default(Status.enum.active),
    ...timestamps(),
    ...userActions(),
});
export const storageLabelsRelations = relations(storageLabels, ({ one }) => ({
    created: one(user, {
        fields: [storageLabels.createdBy],
        references: [user.id],
    }),
}));
export const stabilityRules = pgTable('stability_rules', {
    id: uuidv7('id').primaryKey(),
    carrierDrugId: uuid('carrier_solution_id').references(() => drugCatalogItems.id, {
        onDelete: 'restrict',
        onUpdate: 'cascade',
    }),
    additionalActiveSubstanceDrugId: uuid('additional_active_substance_drug_id').references(() => drugCatalogItems.id, {
        onDelete: 'restrict',
        onUpdate: 'cascade',
    }),
    stabilityStage: stabilityStageEnum('stability_stage').notNull(),
    isDefaultStage: boolean('is_default_stage'),
    startEventNotes: text('start_event_notes'),
    storageLabelId: uuid('storage_label_id').references(() => storageLabels.id, {
        onDelete: 'restrict',
        onUpdate: 'cascade',
    }),
    tempMinC: decimal('temp_min_c', { mode: 'number' }),
    tempMaxC: decimal('temp_max_c', { mode: 'number' }),
    lightProtectionRequired: boolean('light_protection_required'),
    moistureProtectionRequired: boolean('moisture_protection_required'),
    doNotRefrigerate: boolean('do_not_refrigerate'),
    doNotFreeze: boolean('do_not_freeze'),
    controlledTemperature: boolean('controlled_temperature'),
    keepInOriginalPackaging: boolean('keep_in_original_packaging'),
    containerTypeId: uuid('container_type_id').references(() => containerTypes.id, {
        onDelete: 'restrict',
        onUpdate: 'cascade',
    }),
    singleDoseOnly: boolean('single_dose_only'),
    preservativePresent: boolean('preservative_present'),
    requiresAsepticHandling: boolean('requires_aseptic_handling'),
    durationValue: decimal('duration_value', { mode: 'number' }).notNull(),
    durationUnit: durationUnitEnum('duration_unit').notNull(),
    expiryRuleType: expiryRuleTypeEnum('expiry_rule_type').notNull(),
    microbiologicalLimitation: microbiologicalLimitationEnum('microbiological_limitation'),
    discardRemainderRequired: boolean('discard_remainder_required'),
    compatibilityNotes: text('compatibility_notes'),
    notes: text('notes'),
    // preparation labels to print; null falls back to the organisation default
    labelCopies: integer('label_copies'),
    source: stabilityRuleSourceEnum('source').notNull(),
    status: basicDataStatus('status').default(Status.enum.active),
    ...timestamps(),
    ...userActions(),
});
export const stabilityRuleContainerMaterials = pgTable('stability_rule_container_materials', {
    id: uuidv7('id').primaryKey(),
    stabilityRuleId: uuid('stability_rule_id')
        .notNull()
        .references(() => stabilityRules.id, { onDelete: 'cascade', onUpdate: 'cascade' }),
    containerMaterialId: uuid('container_material_id')
        .notNull()
        .references(() => containerMaterials.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    ...timestamps(),
    ...userActions(),
});
export const stabilityRulesRelations = relations(stabilityRules, ({ one, many }) => ({
    containerType: one(containerTypes, {
        fields: [stabilityRules.containerTypeId],
        references: [containerTypes.id],
    }),
    containerMaterials: many(stabilityRuleContainerMaterials),
    carrierDrug: one(drugCatalogItems, {
        fields: [stabilityRules.carrierDrugId],
        references: [drugCatalogItems.id],
    }),
    additionalActiveSubstanceDrug: one(drugCatalogItems, {
        fields: [stabilityRules.additionalActiveSubstanceDrugId],
        references: [drugCatalogItems.id],
    }),
    storageLabel: one(storageLabels, {
        fields: [stabilityRules.storageLabelId],
        references: [storageLabels.id],
    }),
}));
export const stabilityRuleMaterialConstraintsRelations = relations(stabilityRuleContainerMaterials, ({ one }) => ({
    stabilityRule: one(stabilityRules, {
        fields: [stabilityRuleContainerMaterials.stabilityRuleId],
        references: [stabilityRules.id],
    }),
    containerMaterial: one(containerMaterials, {
        fields: [stabilityRuleContainerMaterials.containerMaterialId],
        references: [containerMaterials.id],
    }),
}));
// #endregion
//# sourceMappingURL=basic-data.js.map